import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { useTranslation } from 'react-i18next';
import { showAlert, hideAlert } from '../../../../features/slices/applicationSlice';
import { enqueueSnackbar } from 'notistack';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { useNavigate } from 'react-router-dom';
import {
    useGetCentralOrganismsByContratoQuery,
    useGetCentralOrganismsQuery
} from '../../../../services/organismsApi';
import { generateContractFields } from '../components/generateContractFields';
import ContractSelector, { ContractOption } from '../components/ContractSelector';
import { Box, Button } from '@mui/material';
import {
    useCreateAvisoObraMutation,
    useGetTiposObraQuery,
    useUpdateAvisoObraMutation
} from '../../../../services/avisoObraApi';
import {
    ContratistaFormItem,
    CreateAvisoObraDTO,
    ResponsableFormItem
} from '../../consts/avisoObraTypes';
import { buildMultipartBody } from '../utils/buildMultipartBody';

type FormData = Partial<CreateAvisoObraDTO> & {
    responsables?: ResponsableFormItem[];
    contratistas?: ContratistaFormItem[];
};

type UseCreateAvisoObraProps = {
    isEdit?: boolean;
    initialData?: any;
    isView?: boolean;
};

const useCreateAvisoObra = ({
    isEdit = false,
    isView = false,
    initialData = null
}: UseCreateAvisoObraProps) => {
    const [step, setStep] = useState(1);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation('avisoObra');
    const [createAvisoObra] = useCreateAvisoObraMutation();
    const [isLoadingNext, setIsLoadingNext] = useState(false);
    const userData = useAppSelector((state) => state.auth.user);
    const organismos = userData?.organismos || [];
    const [updateAvisoObra, { isLoading: isSavingDraft }] = useUpdateAvisoObraMutation();

    const [idAvisoObra, setIdAvisoObra] = useState<number | null>(null);

    const { data: tiposObra = [], isLoading } = useGetTiposObraQuery();

    useEffect(() => {
        if ((isEdit || isView) && initialData?.idOrganismoCentral && !selectedContractId) {
            setSelectedContractId(initialData.idOrganismoCentral);
        }
    }, [isEdit, isView, initialData]);

    const contractOptions: ContractOption[] = useMemo(() => {
        const list = (organismos as any[])
            .map((org) => {
                const id = Number(org?.idOrganismoCentral);
                if (!id) return null;
                const nroContrato = org?.nroContrato != null ? String(org.nroContrato) : null;

                const label = nroContrato ?? `ID: ${id}`;
                return { id, label } as ContractOption;
            })
            .filter(Boolean) as ContractOption[];

        const map = new Map<number, ContractOption>();
        for (const it of list) map.set(it.id, it);
        const result = Array.from(map.values());
        return result;
    }, [organismos]);

    const uniqueIds = useMemo(() => contractOptions.map((o) => o.id), [contractOptions]);
    const hasMultipleContracts = uniqueIds.length > 1;

    const [selectedContractId, setSelectedContractId] = useState<number | null>(() => {
        return !hasMultipleContracts && uniqueIds.length === 1 ? uniqueIds[0] : null;
    });

    const [tempSelectedContractId, setTempSelectedContractId] = useState<number | null>(null);

    const activeContractId = selectedContractId ?? (uniqueIds.length === 1 ? uniqueIds[0] : null);

    const activeContractLabel = useMemo(() => {
        const opt = contractOptions.find((o) => o.id === activeContractId);
        return opt?.label ?? undefined;
    }, [activeContractId, contractOptions]);

    const { data: centralContracts, isLoading: loadingContracts } =
        useGetCentralOrganismsByContratoQuery(
            { idOrganizacion: activeContractId! },
            { skip: !activeContractId }
        );

    const findOptionById = (id: number | null): ContractOption | null =>
        id ? contractOptions.find((o) => o.id === id) ?? null : null;

    useEffect(() => {
        if (!isEdit && !isView && hasMultipleContracts && !selectedContractId) {
            const initialId = uniqueIds[0] ?? null;
            setTempSelectedContractId(initialId);

            const selectorElement = React.createElement(ContractSelector as any, {
                options: contractOptions,
                value: findOptionById(initialId),
                onChange: (opt: ContractOption | null) =>
                    setTempSelectedContractId(opt ? opt.id : null),
                label: t('form.contractSelectorLabel', 'Seleccioná un contrato')
            });

            const cancelBtn = React.createElement(
                Button,
                { variant: 'outlined', onClick: () => dispatch(hideAlert()) },
                t('form.cancelar')
            );

            const confirmBtn = React.createElement(
                Button,
                {
                    variant: 'contained',
                    disabled: !tempSelectedContractId,
                    onClick: () => {
                        if (!tempSelectedContractId) return;
                        setSelectedContractId(tempSelectedContractId);
                        dispatch(hideAlert());
                        enqueueSnackbar(t('form.contractSelected', 'Contrato seleccionado'), {
                            variant: 'success'
                        });
                    }
                },
                t('form.confirmarSeleccion', 'Confirmar')
            );

            const buttonsRow = React.createElement(
                Box,
                { display: 'flex', gap: 2, width: '100%', justifyContent: 'center' },
                cancelBtn,
                confirmBtn
            );

            dispatch(
                showAlert({
                    title: t('form.multipleContractsTitle', 'Contratos asociados'),
                    message: t(
                        'form.multipleContracts',
                        'El usuario tiene varios contratos asociados'
                    ),
                    icon: 'info',
                    reactElement: selectorElement,
                    customButtons: buttonsRow,
                    keepMounted: true
                })
            );
        }
    }, [
        hasMultipleContracts,
        selectedContractId,
        uniqueIds,
        contractOptions,
        dispatch,
        t,
        tempSelectedContractId
    ]);

    const selectedContract = useMemo(() => {
        if (!centralContracts?.data?.organismosCentral) return null;
        const organismo = centralContracts.data.organismosCentral;
        return organismo.id === activeContractId ? organismo : null;
    }, [centralContracts, activeContractId]);

    const contractFields = useMemo(
        () => generateContractFields({ contrato: selectedContract || {}, t }),
        [selectedContract, t]
    );

    const [formData, setFormData] = useState<FormData>({});
    const numericFields = [
        'idProvincia',
        'idLocalidad',
        'idTipoResolucion',
        'idOrganismoCentral',
        'idCargo',
        'idRepresentacion',
        'idTipoContratacion',
        'superficieM2',
        'cantidadPlantas',
        'codigoPostal',
        'latitud',
        'longitud'
    ];

    const handleChange = (event: { target: { name: string; value: any } }) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: numericFields.includes(name) && value !== '' ? Number(value) : value
        }));
    };
    const handleCancel = () => {
        if (!isEdit) {
            dispatch(
                showAlert({
                    title: 'No se guardarán los datos',
                    message: 'Si cancelas la carga de la presentación no se guardará el progreso',
                    icon: 'info',
                    confirmText: 'Seguir editando',
                    cancelText: 'Salir sin guardar',
                    confirmAction: () => {
                        dispatch(hideAlert());
                    },
                    cancelAction: () => {
                        dispatch(hideAlert());
                        navigate(-1);
                    }
                })
            );
        } else {
            dispatch(
                showAlert({
                    title: 'No se guardarán los datos',
                    message: 'Si cancelas la carga de la presentación no se guardará el progreso',
                    icon: 'info',
                    confirmText: 'Guardar como borrador',
                    cancelText: 'Salir sin guardar',
                    confirmAction: async () => {
                        try {
                            const body = buildMultipartBody({
                                ...formData,
                                idOrganismoCentral: Number(selectedContract?.id ?? activeContractId)
                            });
                            await updateAvisoObra({
                                idAvisoObra: initialData.id || idAvisoObra,
                                body
                            }).unwrap();

                            enqueueSnackbar('Borrador guardado correctamente.', {
                                variant: 'success'
                            });

                            dispatch(hideAlert());
                            navigate(-1);
                        } catch (error: any) {
                            let backendErrors: string[];

                            if (Array.isArray(error?.data?.message)) {
                                backendErrors = error.data.message;
                            } else if (typeof error?.data?.message === 'string') {
                                backendErrors = [error.data.message];
                            } else {
                                backendErrors = ['Ocurrió un error al guardar el borrador.'];
                            }

                            const formattedMessage = backendErrors.join('\n• ');

                            dispatch(
                                showAlert({
                                    title: 'Error al guardar borrador',
                                    message: '• ' + formattedMessage,
                                    icon: 'error',
                                    confirmText: 'Aceptar',
                                    confirmAction: () => dispatch(hideAlert())
                                })
                            );
                        }
                    },
                    cancelAction: () => {
                        dispatch(hideAlert());
                        navigate(-1);
                    }
                })
            );
        }
    };

    const handleStep1Submit = async () => {
        try {
            setIsLoadingNext(true);

            const body = buildMultipartBody({
                ...formData,
                idOrganismoCentral: Number(selectedContract?.id ?? activeContractId)
            });

            const response = await createAvisoObra(body as unknown as CreateAvisoObraDTO).unwrap();
            setIdAvisoObra(response.data);

            setStep((prev) => prev + 1);
        } catch (error: any) {
            throw error;
        } finally {
            setIsLoadingNext(false);
        }
    };

    const handleFinalSubmit = async () => {
        try {
            const body = buildMultipartBody(
                {
                    ...formData,
                    idOrganismoCentral: Number(selectedContract?.id ?? activeContractId)
                },
                2
            );

            await updateAvisoObra({
                idAvisoObra: initialData.id || idAvisoObra,
                body
            }).unwrap();

            enqueueSnackbar('Aviso de Obra generado correctamente.', { variant: 'success' });
            navigate(-1);
        } catch (error: any) {
            const backendError =
                error?.data?.message || 'Ocurrió un error inesperado al enviar el aviso de obra.';

            dispatch(
                showAlert({
                    title: 'Error al enviar aviso',
                    message: Array.isArray(backendError)
                        ? '• ' + backendError.join('\n• ')
                        : backendError,
                    icon: 'error',
                    confirmText: 'Aceptar',
                    confirmAction: () => dispatch(hideAlert())
                })
            );
        }
    };

    const handleSaveDraft = async (formData: any, avisoObraId: number) => {
        try {
            const body = buildMultipartBody({
                ...formData,
                idOrganismoCentral: Number(selectedContract?.id ?? activeContractId)
            });

            await updateAvisoObra({
                idAvisoObra: avisoObraId,
                body
            }).unwrap();

            enqueueSnackbar('Borrador guardado correctamente.', { variant: 'success' });
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo guardar el borrador del aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return {
        isEdit,
        isView,
        idAvisoObra,
        step,
        setStep,
        formData,
        handleChange,
        handleCancel,
        isNextDisabled: false,
        loading: false,
        hasMultipleContracts,
        contractFields,
        loadingContracts,
        handleStep1Submit,
        tiposObra,
        handleFinalSubmit,
        handleSaveDraft,
        isSavingDraft,
        isLoadingNext
    };
};

export default useCreateAvisoObra;
